/**
 * @file Utility function to transform every single value of an object, leaving
 * the keys intact.
 */

/**
 * Transforms every single value of an object, leaving the keys intact.
 * @param object The object to transform
 * @param transform The function mapping the old value to the new value
 * @returns A new object whose values are the result of the transformation of
 * the corresponding key from the original object
 * @remarks This could have easily been imported from `lodash-es` but it's
 * inlined here to keep this package dependency-free.
 */

function mapValues<TObject extends object, TNewValue>(
  object: TObject,
  transform: (value: TObject[keyof TObject], key: keyof TObject) => TNewValue,
): {
  [TKey in keyof TObject]: TNewValue
} {
  const mappedObject = {} as Partial<{ [TKey in keyof TObject]: TNewValue }>
  for (const key in object) {
    if (Object.prototype.hasOwnProperty.call(object, key)) {
      mappedObject[key] = transform(object[key], key)
    }
  }
  return mappedObject as { [TKey in keyof TObject]: TNewValue }
}

export default mapValues
